import {
  RestaurantListData,
  salesPortalRestaurantRowTransformer,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/sales-portal-restaurant-transformer';
import { useGeneratedSalesPortalSelectedRestaurantQuery } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/documents/sales-portal-selected-restaurant.generated';
import { useMemo } from 'react';

/**
 * Fetches the selected restaurant.
 *
 * @param restaurantUUID - UUID of the selected restaurant
 * @param supplierUUID - UUID of the selected supplier
 * @returns The transformed `selectedRestaurant` (`undefined` while loading or
 *   when no restaurant matches the pair), plus Apollo `loading`, `error` and `refetch`.
 *
 * @example
 * const { selectedRestaurant, loading, error, refetch } =
 *   useSalesPortalSelectedRestaurantLoader(restaurantUUID, supplierUUID);
 */
export const useSalesPortalSelectedRestaurantLoader = (
  restaurantUUID: string | undefined,
  supplierUUID: string | undefined,
) => {
  const shouldSkip = !restaurantUUID || !supplierUUID;

  const { data, loading, error, refetch } =
    useGeneratedSalesPortalSelectedRestaurantQuery({
      fetchPolicy: 'cache-and-network',
      skip: shouldSkip,
      variables:
        !restaurantUUID || !supplierUUID
          ? undefined
          : { restaurantUUID, supplierUUID },
    });
  const chat = data?.chats[0];

  const selectedRestaurant = useMemo<RestaurantListData | undefined>(
    () =>
      chat
        ? salesPortalRestaurantRowTransformer({
            chatUUID: chat.uuid,
            onHold: chat.on_hold,
            restaurant: chat.restaurant,
            supplier: chat.supplier,
          })
        : undefined,
    [chat],
  );

  return {
    error,
    loading: shouldSkip || loading,
    refetch,
    selectedRestaurant,
  };
};
